/**
 * Daily Cockpit — the front-desk staff's single daily starting view.
 *
 * Shows the next 7 days as a selectable day rail; pick any day to see that
 * day's patients with their eligibility status so staff can see who's covered
 * and who needs attention before they walk in the door.
 *
 * Reads real appointment data from Firestore:
 *   organizations/{orgId}/clinics/{clinicId}/appointments/{identityHash}
 *
 * Each row reflects the latest eligibility check in its flow (`flowId` — the
 * row's linear correction chain), so a corrected re-run supersedes the failure
 * even when the correction changed identity fields (DOB / member ID / payer /
 * provider) and landed in a different dedup set.
 * A definitive result (eligible / ineligible) decays to a "Stale" badge once the
 * visit it was run for has passed. The date of service is resolved from the
 * check's own `appointmentDate`, falling back to this appointment's date when
 * it's the appointment's own check, else to when the check ran (see
 * shared/eligibilityFreshness.resolveDateOfService). needs-review and error
 * states show regardless. A newer check in the flow always supersedes the old one.
 */

import { useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { scrubVendorText } from '../utils/scrubVendorText'
import type { AppointmentPatient, Clinic, EligibilityCheckDoc, Provider } from '../types'
import {
  subscribeToAppointmentPatients,
  computeSummary,
  formatCockpitDate,
  parseIsoDateStr,
  parseFirestoreDateStr,
  firestoreDateToIso,
  toFirestoreDateStr,
  initiateAppointmentPull,
  type CockpitSummary,
} from '../services/appointments'
import { subscribeToLatestChecksByFlowIds, checkStatusToCockpit } from '../services/eligibilityChecks'
import {
  DISPLAY_STATUS_META, ROLLUP_OF, ROLLUP_GROUP_META, ROLLUP_ORDER,
  isAutoRetrying, retryingReason, retryingStatusMeta,
  type DisplayStatusMeta, type StatusIconKey, type EligibilityDisplayStatus, type RollupGroup,
} from '../../shared/eligibilityDisplayStatus'
import { formatAppointmentTimeRange, stripPayerIdSuffix } from '../utils/formatters'
import { reasonCopyFor } from '../../shared/terminalState'
import { buildPatientSearchMatcher } from '../utils/patientSearch'
import { groupPatients, countPatients } from '../utils/patientGrouping'
import { logPhiView } from '../services/audit'
import { readTerminal } from '../utils/terminalStateBadge'
import { computeBatchRollups, batchKeyOf, hasLiveCsvBatch, selectBandNow, BATCH_WINDOW_MS, BATCH_CLOCK_RESOLUTION_MS, type BatchRollup } from '../utils/batchRollup'
import { useNow } from '../hooks/useNow'
import { payerProvenance, type PayerProvenance } from '../utils/payerProvenance'
import BatchProgressBand from './BatchProgressBand'
import { IconCheckCircle, IconXCircle, IconAlertTriangle, IconClock, IconSearch, IconCalendar, IconRefresh, IconSparkle } from './Icons'

// ── Props ────────────────────────────────────────────────────────────

interface DailyCockpitProps {
  orgId: string
  /** The working clinic. Null only when the org has no active clinic — the
   *  parent renders an empty state for clinic-less non-admins, and the admin
   *  paths here never build a clinic-scoped query from it. */
  effectiveClinicId: string | null
  isAdmin: boolean
  clinics: Clinic[]
  /** True once the clinics fetch has resolved. Without this, the revolution-integration
   *  filter (`revolutionClinicIds`) would drop every patient row on first paint, briefly
   *  flashing the "No appointments loaded" empty state before clinics arrive. */
  clinicsLoaded: boolean
  /** Provider list — used to resolve provider names on Stedi checks (where the check stores providerId, not the name). */
  providers: Provider[]
  showClinicFilter: boolean
  /** Navigate to the eligibility form to run a new check for this patient. */
  onRunCheck?: (patient: AppointmentPatient) => void
  /** View completed check results for a patient. */
  onViewCheck?: (patient: AppointmentPatient, check: EligibilityCheckDoc) => void
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Format DOB (YYYY-MM-DD, normalized at ingestion) as MM/DD/YYYY for display. */
function formatDob(dob: string): string {
  try {
    const d = parseIsoDateStr(dob)
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${mm}/${dd}/${d.getFullYear()}`
  } catch {
    return dob
  }
}

/** Format appointment date (MM/DD/YYYY from Firestore) as "Thu, Mar 5". */
function formatAppointmentDate(dateStr: string): string {
  try {
    return parseFirestoreDateStr(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  } catch {
    return dateStr
  }
}

/** "3 patients" / "1 patient". */
function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

/** "MATHISEN" / "mathisen" → "Mathisen". Handles multi-word and hyphenated names. */
function toProperCase(s: string): string {
  return s.toLowerCase().replace(/(?:^|\s|-)(\w)/g, m => m.toUpperCase())
}

/**
 * Resolve a display-ready provider name from an eligibility check.
 * Mirrors the logic in RecentChecksTable so both views stay consistent.
 *  1. Stedi: providers lookup by `check.providerId` → "Lastname, Firstname"
 *  2. VSP/EyeMed RPA: `check.identity.doctorName` with the trailing "(NPI-…, Tax ID-…)" stripped
 *  3. Otherwise: undefined (caller falls back to appointment data)
 */
function providerNameFromCheck(check: EligibilityCheckDoc, providersById: Map<string, Provider>): string | undefined {
  const provider = check.providerId ? providersById.get(check.providerId) : undefined
  if (provider) return `${toProperCase(provider.lastName)}, ${toProperCase(provider.firstName)}`
  const raw = check.identity?.doctorName?.replace(/\s*\(.*\)$/, '').trim()
  return raw ? toProperCase(raw) : undefined
}

// ── Batch band dismissal (localStorage) ──────────────────────────────
// A finished batch band stays pinned until dismissed (or until it ages past
// the 24h window). Dismissals are per-browser — that matches the band's job
// (the person watching this screen is done with it), and nothing is lost:
// the rows themselves stay on the Cockpit.

const BATCH_DISMISS_KEY = 'vr:batchBandDismissed:v1'

function readDismissedBatches(): Record<string, number> {
  try {
    const raw = localStorage.getItem(BATCH_DISMISS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    // Prune entries past the band window so the key doesn't grow forever.
    const cutoff = Date.now() - BATCH_WINDOW_MS
    const out: Record<string, number> = {}
    for (const [k, ts] of Object.entries(parsed)) {
      if (typeof ts === 'number' && ts > cutoff) out[k] = ts
    }
    return out
  } catch {
    return {}
  }
}

// ── Status Badge ────────────────────────────────────────────────────
// Renders the detailed row-level status from the canonical display-status meta
// (shared/eligibilityDisplayStatus) so the cockpit, Recent Checks, and the
// failure slideout all read identical labels/icons/colors from one source.
// `cockpit-status` keeps `white-space: nowrap`, so labels need no &nbsp;.

const STATUS_ICON: Record<StatusIconKey, ReactNode> = {
  check: <IconCheckCircle />,
  x: <IconXCircle />,
  alert: <IconAlertTriangle />,
  refresh: <IconRefresh />,
  clock: <IconClock />,
  spinner: <span className="cockpit-status__spinner" />,
}

function CockpitStatusBadge({ status, title, override }: {
  status: AppointmentPatient['eligibilityStatus']
  title?: string
  /** Label/icon replacement for a status whose default presentation would lie
   *  about the row — the auto-retry case (#289): the check maps to in_progress
   *  but no work is running, so the badge says failed-and-waiting instead.
   *  Tone still follows the status's rollup group, matching the glance. */
  override?: DisplayStatusMeta
}) {
  const s = status ?? 'not_checked'
  const meta = override ?? DISPLAY_STATUS_META[s]
  // Chip color follows the rollup group, so the row matches the glance:
  // Ineligible reads green (Resolved), Error reads red (Broken), etc.
  return (
    <>
      <span className={`cockpit-status cockpit-status--${ROLLUP_OF[s]}`} title={title || undefined}>
        {STATUS_ICON[meta.iconKey]} {meta.label}
      </span>
      {/* Only the auto-retry override carries a second line: when the next
          attempt is due (#289). Fixed statuses have nothing to add. */}
      {meta.sublabel && <div className="cockpit__cell-secondary">{meta.sublabel}</div>}
    </>
  )
}

// ── Day rail ────────────────────────────────────────────────────────

/** One selectable day in the 7-day rail. */
interface WeekDay {
  /** MM/DD/YYYY — matches `AppointmentPatient.appointmentDate`, used as the bucket key. */
  key: string
  /** "Today" / "Tomorrow" for the first two days, "" otherwise. */
  label: string
  /** "Mon" */
  weekday: string
  /** "Jun 22" */
  monthDay: string
}

/** Hover detail for a rollup segment: the per-status breakdown that rolls up
 *  into this color, e.g. "12 Eligible · 3 Ineligible". Empty groups read
 *  "No <group>" so the tooltip is never blank. */
function groupBreakdown(group: RollupGroup, byStatus: Record<EligibilityDisplayStatus, number>): string {
  const parts = (Object.keys(byStatus) as EligibilityDisplayStatus[])
    .filter(s => ROLLUP_OF[s] === group && byStatus[s] > 0)
    .map(s => `${byStatus[s]} ${DISPLAY_STATUS_META[s].label}`)
  return parts.length > 0 ? parts.join(' · ') : `No ${ROLLUP_GROUP_META[group].label.toLowerCase()}`
}

function DayTile({
  day,
  summary,
  isActive,
  onClick,
}: {
  day: WeekDay
  summary: CockpitSummary
  isActive: boolean
  onClick: () => void
}) {
  const counts: Record<RollupGroup, number> = {
    resolved: summary.resolved,
    attention: summary.attention,
    broken: summary.broken,
    neutral: summary.neutral,
  }
  const { total, byStatus } = summary
  const pct = (n: number) => total > 0 ? (n / total) * 100 : 0

  return (
    <button
      className={`cockpit-day-tile ${isActive ? 'cockpit-day-tile--active' : ''} ${total === 0 ? 'cockpit-day-tile--empty' : ''}`}
      onClick={onClick}
      type="button"
    >
      {/* Eyebrow keeps its height even when empty so all 7 tiles align. */}
      <span className="cockpit-day-tile__eyebrow">{day.label}</span>
      <span className="cockpit-day-tile__date">
        <strong>{day.weekday}</strong> <span>· {day.monthDay}</span>
      </span>
      {/* Count = distinct humans (see CockpitSummary.patients); the bar below
          still segments by coverage-row status — a status belongs to a check,
          not a person. */}
      <span className="cockpit-day-tile__stat">
        <span className="cockpit-day-tile__count">{summary.patients}</span>
        <span className="cockpit-day-tile__unit">patients</span>
      </span>
      {/* 3-color rollup glance: 🟢 resolved / 🟡 attention / 🔴 broken / ⚪ neutral.
          Reuses the horizon-card segment tone classes so the colors stay in one place. */}
      <span className="cockpit-day-tile__bar">
        {ROLLUP_ORDER.map(g => counts[g] > 0 ? (
          <span
            key={g}
            className={`cockpit-horizon-card__bar-seg cockpit-horizon-card__bar-seg--${ROLLUP_GROUP_META[g].tone}`}
            style={{ width: `${pct(counts[g])}%` }}
            title={groupBreakdown(g, byStatus)}
          />
        ) : null)}
      </span>
    </button>
  )
}

type SortKey = 'patient' | 'payer' | 'status' | 'provider' | 'appointmentDate'

/**
 * Cockpit-local enrichment over the stored AppointmentPatient shape.
 *
 * `displayPayer` is the payer name actually shown in the row — it prefers the
 * linked check's resolved `payerName` (e.g. "Medicare WA Part B" after the
 * sweep rewrote a raw "Medicare") and falls back to the raw appointment value
 * when no check has completed yet.
 *
 * The badge gate is `payerDecidedBy`, not a separate boolean: 'ai' shows the
 * sparkle, 'staff' the human-correction marker, null neither. An `isResolved`
 * flag used to be computed here and described as the gate; nothing ever read
 * it, and a docstring naming a field that decides nothing is worse than no
 * docstring (#314 review).
 */
interface EnrichedAppointmentPatient extends AppointmentPatient {
  displayPayer: string | null
  /** Who decided the displayed payer — 'ai' shows the sparkle, 'staff' the
   *  human-correction marker, null neither (#314 AC8). */
  payerDecidedBy: PayerProvenance
}

function SortableTh({ label, field, sortKey, sortDirection, onSort }: {
  label: string
  field: SortKey
  sortKey: SortKey
  sortDirection: 'asc' | 'desc'
  onSort: (key: SortKey) => void
}) {
  return (
    <th
      className={`sortable-th ${sortKey === field ? 'sortable-th--active' : ''}`}
      onClick={() => onSort(field)}
    >
      {label}
      {sortKey === field && (
        <span className="sort-arrow">{sortDirection === 'desc' ? '↓' : '↑'}</span>
      )}
    </th>
  )
}

const COLLATOR = new Intl.Collator('en', { sensitivity: 'base' })

// ── URL view state ──────────────────────────────────────────────────
// The cockpit's view state (selected day, status filter, sort) is mirrored
// into the URL query string so a browser refresh — or a pasted link — lands
// on the same view instead of resetting to today/unfiltered/status-sort.
// Search text is deliberately NOT mirrored: queries can contain PHI (names,
// DOBs) and query strings reach request logs and browser history.
const URL_SORT_KEYS: SortKey[] = ['patient', 'payer', 'status', 'provider', 'appointmentDate']

function readUrlViewState() {
  const params = new URLSearchParams(window.location.search)
  const status = params.get('status')
  const sort = params.get('sort')
  const dir = params.get('dir')
  return {
    day: params.get('day'),
    rollupFilter: status && (ROLLUP_ORDER as string[]).includes(status) ? status as RollupGroup : null,
    sortKey: sort && (URL_SORT_KEYS as string[]).includes(sort) ? sort as SortKey : null,
    sortDirection: dir === 'asc' ? 'asc' as const : dir === 'desc' ? 'desc' as const : null,
  }
}

// Default status sort leads with the green/resolved rollup so the cockpit
// opens on "who's covered" — eligible first, then ineligible (also green) —
// then attention (needs review / stale), then broken (data issue / error),
// with the neutral not-yet-run states (in progress / not checked) last.
const STATUS_RANK: Record<string, number> = {
  eligible: 0,
  not_eligible: 1,
  needs_review: 2,
  stale: 3,
  data_issue: 4,
  error: 5,
  in_progress: 6,
  not_checked: 7,
}

// ── Main Component ──────────────────────────────────────────────────

export default function DailyCockpit({
  orgId,
  effectiveClinicId,
  isAdmin,
  clinics,
  clinicsLoaded,
  providers,
  showClinicFilter,
  onRunCheck,
  onViewCheck,
}: DailyCockpitProps) {
  // Frozen per mount — overnight tabs re-mount via auth state listener
  const baseDate = useMemo(() => new Date(), []) // stable per mount

  // ── State ────────────────────────────────────────────────────────
  // Selected day, keyed by MM/DD/YYYY (matches appointmentDate). Defaults to
  // today; a `?day=` param inside this mount's 7-day window wins (refresh /
  // shared link restores the view — a stale out-of-window day falls back).
  const [selectedDate, setSelectedDate] = useState(() => {
    const fromUrl = readUrlViewState().day
    if (fromUrl) {
      for (let i = 0; i < 7; i++) {
        const d = new Date(baseDate)
        d.setDate(d.getDate() + i)
        if (toFirestoreDateStr(d) === fromUrl) return fromUrl
      }
    }
    return toFirestoreDateStr(baseDate)
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [clinicFilter, setClinicFilter] = useState<'all' | string>('all')
  // Status quick-filter: narrows the selected day's table to one rollup group.
  const [rollupFilter, setRollupFilter] = useState<'all' | RollupGroup>(() => readUrlViewState().rollupFilter ?? 'all')
  const [sortKey, setSortKey] = useState<SortKey>(() => readUrlViewState().sortKey ?? 'status')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => readUrlViewState().sortDirection ?? 'asc')
  const [isRepulling, setIsRepulling] = useState(false)
  // Batch progress band (#113): filter the cockpit to one CSV upload, and
  // remember which finished bands the user has dismissed.
  const [batchFilter, setBatchFilter] = useState<string | null>(null)
  const [dismissedBatches, setDismissedBatches] = useState<Record<string, number>>(readDismissedBatches)

  // HIPAA PHI-access trail (#185): opening (or switching to) a day of the
  // schedule is a PHI view — the day's roster shows patient names, DOBs and
  // coverage. Deduped per day per session inside logPhiView.
  // A multi-clinic admin on "All Clinics" sees an org-wide union: that view has
  // no single clinic, and naming one (the admin's home clinic) would file the
  // entry under the wrong location — worse than naming none. Org-wide views
  // carry no clinicId. (Mirrors the repullClinicId selection below.)
  useEffect(() => {
    // Wait for clinics so the first log carries the resolved clinic — logging
    // during the load window would file a spurious org-wide entry, then a
    // second clinic-scoped one after resolution (the dedup key includes
    // clinicId, so both would persist).
    if (!clinicsLoaded) return
    const viewedClinicId = isAdmin && showClinicFilter
      ? (clinicFilter !== 'all' ? clinicFilter : null)
      : effectiveClinicId
    logPhiView('appointment_day', selectedDate, viewedClinicId)
  }, [selectedDate, clinicFilter, isAdmin, showClinicFilter, effectiveClinicId, clinicsLoaded])

  // Mirror view state back to the URL (replaceState — no history entries).
  // Params are dropped at their defaults so the address bar stays clean, and
  // the `token`/`check` deep-link params owned by App.tsx are left untouched.
  useEffect(() => {
    const url = new URL(window.location.href)
    const setOrDelete = (key: string, value: string | null) => {
      if (value === null) url.searchParams.delete(key)
      else url.searchParams.set(key, value)
    }
    setOrDelete('day', selectedDate === toFirestoreDateStr(baseDate) ? null : selectedDate)
    setOrDelete('status', rollupFilter === 'all' ? null : rollupFilter)
    setOrDelete('sort', sortKey === 'status' ? null : sortKey)
    setOrDelete('dir', sortDirection === 'asc' ? null : sortDirection)
    window.history.replaceState(window.history.state, '', url.toString())
  }, [selectedDate, rollupFilter, sortKey, sortDirection, baseDate])

  // ── Firestore subscription ────────────────────────────────────────
  const [allPatients, setAllPatients] = useState<AppointmentPatient[]>([])
  const [hasReceivedData, setHasReceivedData] = useState(false)
  // Also wait on clinics: the revolution-integration filter below drops every row
  // until `clinics` is hydrated, otherwise the empty state flashes before the table.
  const loading = !hasReceivedData || !clinicsLoaded

  useEffect(() => {
    // Null-contract belt: a non-admin with no clinic has no roster to
    // subscribe to (the parent gates this view, but never build the query).
    if (!isAdmin && !effectiveClinicId) { setHasReceivedData(true); return }
    let first = true
    const unsub = subscribeToAppointmentPatients(
      orgId, effectiveClinicId, isAdmin, baseDate,
      (patients) => {
        setAllPatients(patients)
        if (first) { setHasReceivedData(true); first = false }
      },
      (err) => {
        Sentry.captureException(err)
        if (first) { setHasReceivedData(true); first = false }
      },
    )
    return () => {
      unsub()
      setHasReceivedData(false)
      setAllPatients([])
    }
  }, [orgId, effectiveClinicId, isAdmin, baseDate])

  // ── Eligibility check subscription ──────────────────────────────
  // Keyed by flowId — the row's linear correction chain. The newest check in
  // the chain drives the row even after staff corrections (DOB / member ID /
  // payer / provider) move later checks into a different dedup set.
  const [checksByFlowId, setChecksByFlowId] = useState<Map<string, EligibilityCheckDoc>>(new Map())

  // Stabilize as a sorted, deduped string so the effect only re-fires when flow IDs actually change
  const flowIdsKey = useMemo(() => {
    const ids = new Set<string>()
    for (const p of allPatients) {
      if (p.flowId) ids.add(p.flowId)
    }
    return [...ids].sort().join('\n')
  }, [allPatients])

  useEffect(() => {
    const flowIds = flowIdsKey ? flowIdsKey.split('\n') : []
    return subscribeToLatestChecksByFlowIds(
      orgId, flowIds, setChecksByFlowId, isAdmin ? null : effectiveClinicId,
    )
  }, [orgId, flowIdsKey, isAdmin, effectiveClinicId])

  const providersById = useMemo(() => new Map(providers.map(p => [p.id, p])), [providers])

  // Set of clinic IDs that have a configured RevolutionEHR integration.
  // Used to hide stale Revolution-sourced rows from clinics whose integration
  // has been removed — those docs linger after the config is deleted because
  // nothing repulls/cancels them. CSV uploads are explicit user actions and
  // bypass this filter regardless of clinic integration state.
  const revolutionClinicIds = useMemo(
    () => new Set(clinics.filter(c => c.integrations?.revolution_ehr).map(c => c.id)),
    [clinics],
  )

  // Drop stale Revolution rows (clinic no longer integrated), then enrich with
  // real-time eligibility status from the most recent check per set.
  // Provider name preference: check (providers lookup → identity.doctorName,
  // parens stripped) → appointment data.
  // Payer name preference: check.payerName (the resolved name from Stedi after
  // the sweep ran, e.g. "Medicare WA Part B") → appointment.payer (raw, e.g.
  // "Medicare"). The provenance flips on only when both sides were non-empty AND
  // differ (case-insensitive); we use it to render the "Suggested by VitalRev
  // AI" badge so a vendor-name fallback (RPA defaulting to "VSP" when
  // Revolution sent nothing) doesn't trip the indicator.
  const enrichedPatients = useMemo(() => {
    const out: EnrichedAppointmentPatient[] = []
    for (const p of allPatients) {
      if (!p.clinicId) continue
      if (p.source !== 'csv' && !revolutionClinicIds.has(p.clinicId)) continue
      const check = p.flowId ? checksByFlowId.get(p.flowId) : undefined
      const rawPayer = stripPayerIdSuffix((p.payer ?? '').trim())
      const checkPayer = stripPayerIdSuffix(check?.payerName?.trim() ?? '')
      const displayPayer = checkPayer || rawPayer || null
      // An alias-learning sweep recheck (ALIAS_PROBE_MODE) is AI-resolved even
      // when it stores payerName == the shorthand (so the name comparison misses
      // it) — flag the mode explicitly.
      // Mode first: a staff correction preserves the raw shorthand by design,
      // so the name comparison alone would credit a person's edit to the AI.
      const payerDecidedBy = payerProvenance({
        mode: check?.mode, resolvedPayer: checkPayer, rawPayer,
      })
      const enriched: EnrichedAppointmentPatient = check ? {
        ...p,
        eligibilityStatus: checkStatusToCockpit(check, {
          eligibilityCheckId: p.eligibilityCheckId,
          appointmentDate: p.appointmentDate ? firestoreDateToIso(p.appointmentDate) : null,
        }),
        providerName: providerNameFromCheck(check, providersById) ?? p.providerName,
        displayPayer,
        payerDecidedBy,
      } : { ...p, displayPayer, payerDecidedBy }
      out.push(enriched)
    }
    return out
  }, [allPatients, revolutionClinicIds, checksByFlowId, providersById])

  // ── Batch progress bands (#113) ──────────────────────────────────
  // Roll recent CSV uploads into per-batch counts. Derived from the same
  // enriched rows the table renders (unfiltered by search/clinic — a batch
  // is a batch), so the bands update live off the existing listeners.
  // A coarse 30s clock tick keeps the time-derived pieces honest between
  // Firestore snapshots: the ETA keeps counting down on a quiet screen
  // (slow RPA checks can go minutes without a doc write) and finished
  // bands age out of the 24h window without needing an appointment write.
  // The clock is the shared useNow store, subscribed ONLY while a band can
  // actually render — a CSV row whose upload is inside the 24h window (plus
  // one tick of pad; see hasLiveCsvBatch). Merely having a CSV row somewhere
  // in the 7-day view is NOT enough: that would keep the timer (and its
  // full-cockpit re-render) alive forever. Deliberately plain per-render
  // derivations, not useMemo — window membership depends on the clock the
  // result gates, so it must re-evaluate on every tick-driven render: the
  // tick after the last band expires flips it false and the timer
  // unsubscribes; while unsubscribed, a new upload changes enrichedPatients,
  // this re-evaluates against the wall clock, and the timer resubscribes.
  // The gate and the roll-up must judge the 24h window against ONE clock:
  // a single wall reading per render gates the subscription, and whenever
  // the gate leaves the clock off, selectBandNow ages the roll-ups with that
  // same reading instead of useNow's parked snapshot — otherwise a batch
  // that crossed 24h while the Cockpit was unmounted renders off the stale
  // snapshot forever, with no tick ever coming to expire it.
  const wallNow = Date.now()
  const bandClockOn = hasLiveCsvBatch(enrichedPatients, wallNow)
  const nowTick = useNow(bandClockOn ? BATCH_CLOCK_RESOLUTION_MS : null)
  const bandNow = selectBandNow(bandClockOn, nowTick, wallNow)
  const batchRollups = useMemo(
    () => computeBatchRollups(enrichedPatients, bandNow),
    [enrichedPatients, bandNow],
  )

  const batchBands = useMemo(
    () => batchRollups.filter(b => !(b.key in dismissedBatches)).slice(0, 3),
    [batchRollups, dismissedBatches],
  )

  // Drop the batch filter if its batch aged out of the window.
  useEffect(() => {
    if (batchFilter && !batchRollups.some(b => b.key === batchFilter)) {
      setBatchFilter(null)
    }
  }, [batchFilter, batchRollups])

  const dismissBatch = useCallback((key: string) => {
    setDismissedBatches(prev => {
      const next = { ...prev, [key]: Date.now() }
      try { localStorage.setItem(BATCH_DISMISS_KEY, JSON.stringify(next)) } catch { /* private mode etc. — band just reappears next visit */ }
      return next
    })
    setBatchFilter(f => (f === key ? null : f))
  }, [])

  // Rough time-remaining estimate from throughput observed in this session:
  // baseline (first sight of the batch) → completions since → rate. Shown only
  // once ≥2 completions have landed over ≥15s, so we never display a guess.
  // Baselines are seeded in an effect keyed on the visible bands (never
  // during render — refs must not be written while rendering); until a
  // band's baseline exists, etaFor just returns null (no ETA yet).
  const etaBaselines = useRef(new Map<string, { t0: number; done0: number }>())
  useEffect(() => {
    const visible = new Set<string>()
    for (const b of batchBands) {
      visible.add(b.key)
      if (!b.finished && !etaBaselines.current.has(b.key)) {
        etaBaselines.current.set(b.key, { t0: Date.now(), done0: b.answered + b.cantCheck })
      }
    }
    // Drop baselines for bands no longer shown (dismissed / aged out) so the
    // map can't grow unbounded; a band that reappears simply re-baselines.
    for (const key of [...etaBaselines.current.keys()]) {
      if (!visible.has(key)) etaBaselines.current.delete(key)
    }
  }, [batchBands])
  // `bandNow` in the deps re-derives the estimate on the 30s clock, not just
  // on data changes — without it a "~1 min left" label would sit frozen
  // while slow RPA checks run.
  const etaFor = useCallback((b: BatchRollup): number | null => {
    if (b.finished) return null
    const base = etaBaselines.current.get(b.key)
    if (!base) return null
    const done = b.answered + b.cantCheck
    const progressed = done - base.done0
    const elapsedMs = Math.max(0, bandNow - base.t0)
    if (progressed < 2 || elapsedMs < 15_000) return null
    const remaining = b.total - done
    return remaining > 0 ? (remaining * elapsedMs) / progressed : null
  }, [bandNow])

  // ── The next 7 days ───────────────────────────────────────────────
  const dateLabel = useMemo(() => formatCockpitDate(baseDate), [baseDate])

  const weekDays = useMemo<WeekDay[]>(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(baseDate)
      d.setDate(d.getDate() + i)
      return {
        key: toFirestoreDateStr(d),
        label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : '',
        weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
        monthDay: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      }
    })
  }, [baseDate])

  const selectedDay = useMemo(
    () => weekDays.find(w => w.key === selectedDate) ?? weekDays[0],
    [weekDays, selectedDate],
  )

  // ── Search + clinic filter ────────────────────────────────────────
  // Applied before bucketing so the rail counts AND the table reflect both
  // filters together. Matching (tokenized any-order name, DOB in common
  // formats, separator-blind member ID, payer, provider) lives in
  // utils/patientSearch.ts, shared with Recent Checks.
  const searchClinicFiltered = useMemo(() => {
    const matches = buildPatientSearchMatcher(searchQuery)
    return enrichedPatients.filter(p => {
      if (clinicFilter !== 'all' && p.clinicId !== clinicFilter) return false
      if (!matches) return true
      return matches({
        firstName: p.firstName,
        lastName: p.lastName,
        dob: p.dob,
        memberId: p.policyNumber,
        text: [p.displayPayer, p.providerName],
      })
    })
  }, [enrichedPatients, clinicFilter, searchQuery])

  // The batch filter narrows further — also before bucketing, deliberately:
  // the rail then shows exactly which days this upload spans.
  const visiblePatients = useMemo(
    () => (batchFilter
      ? searchClinicFiltered.filter(p => batchKeyOf(p) === batchFilter)
      : searchClinicFiltered),
    [searchClinicFiltered, batchFilter],
  )

  // Unfiltered count for the "All appointments (N)" chip while a batch filter is on.
  const selectedDayAllCount = useMemo(
    () => searchClinicFiltered.filter(p => p.appointmentDate === selectedDate).length,
    [searchClinicFiltered, selectedDate],
  )

  // ── Per-day buckets + summaries (for the rail) ────────────────────
  const dayBuckets = useMemo(() => {
    const m = new Map<string, EnrichedAppointmentPatient[]>()
    for (const w of weekDays) m.set(w.key, [])
    for (const p of visiblePatients) {
      if (!p.appointmentDate) continue
      const bucket = m.get(p.appointmentDate)
      if (bucket) bucket.push(p)
    }
    return m
  }, [visiblePatients, weekDays])

  const daySummaries = useMemo(() => {
    const m = new Map<string, CockpitSummary>()
    for (const w of weekDays) m.set(w.key, computeSummary(dayBuckets.get(w.key) ?? []))
    return m
  }, [dayBuckets, weekDays])

  const selectedDayPatients = useMemo(
    () => dayBuckets.get(selectedDate) ?? [],
    [dayBuckets, selectedDate],
  )

  // Unfiltered summary of the selected day — feeds the quick-filter chip counts.
  const selectedDaySummary = useMemo(
    () => daySummaries.get(selectedDate) ?? computeSummary([]),
    [daySummaries, selectedDate],
  )

  // ── Status quick-filter ───────────────────────────────────────────
  // Chips in the table header narrow the selected day to one rollup group so
  // staff can pull "who needs work" out of a full schedule instead of reading
  // every row (#179). Rail counts stay unfiltered — they're the ambient
  // readout the chips' own counts come from.
  const filteredDayPatients = useMemo(
    () => rollupFilter === 'all'
      ? selectedDayPatients
      : selectedDayPatients.filter(p => ROLLUP_OF[p.eligibilityStatus ?? 'not_checked'] === rollupFilter),
    [selectedDayPatients, rollupFilter],
  )

  const sortedPatients = useMemo(() => {
    if (filteredDayPatients.length <= 1) return filteredDayPatients
    const dirMultiplier = sortDirection === 'asc' ? 1 : -1

    // Pre-compute date timestamps so the comparator doesn't create Date objects per comparison
    const dateCache = new Map<string, number>()
    const getDateTs = (d?: string) => {
      if (!d) return 0
      let ts = dateCache.get(d)
      if (ts === undefined) { ts = parseFirestoreDateStr(d).getTime(); dateCache.set(d, ts) }
      return ts
    }

    return [...filteredDayPatients].sort((a, b) => {
      let cmp = 0

      switch (sortKey) {
        case 'patient':
          cmp = COLLATOR.compare(
            `${a.lastName}, ${a.firstName}`,
            `${b.lastName}, ${b.firstName}`,
          )
          break
        case 'payer':
          cmp = COLLATOR.compare(a.displayPayer ?? '', b.displayPayer ?? '')
          break
        case 'status':
          cmp = (STATUS_RANK[a.eligibilityStatus ?? ''] ?? 999) - (STATUS_RANK[b.eligibilityStatus ?? ''] ?? 999)
          break
        case 'provider':
          cmp = COLLATOR.compare(a.providerName ?? '', b.providerName ?? '')
          break
        case 'appointmentDate':
        default:
          cmp = getDateTs(a.appointmentDate) - getDateTs(b.appointmentDate)
          break
      }

      // Tie-break: appointment start time (nulls last) → patient name
      if (cmp === 0) {
        const aMin = a.appointmentStartMin ?? null
        const bMin = b.appointmentStartMin ?? null
        if (aMin == null && bMin != null) cmp = 1
        else if (aMin != null && bMin == null) cmp = -1
        else if (aMin != null && bMin != null) cmp = aMin - bMin
      }
      if (cmp === 0 && sortKey !== 'patient') {
        cmp = COLLATOR.compare(
          `${a.lastName}, ${a.firstName}`,
          `${b.lastName}, ${b.firstName}`,
        )
      }

      return cmp * dirMultiplier
    })
  }, [filteredDayPatients, sortKey, sortDirection])

  // ── Patient grouping ──────────────────────────────────────────────
  // One human with several coverage rows (medical + vision, or two providers
  // that day) renders as ONE patient cell over N coverage sub-rows, so the
  // "patients" count means humans. Grouping runs after sort: a group sits
  // where the sort placed its first row and pulls its later rows up next to
  // it (a grouped table can't preserve a pure per-row sort). The key is
  // conservative — clinic+day+name+DOB all present, or the row stands alone
  // (see utils/patientGrouping).
  const renderRows = useMemo(() => {
    const out: {
      patient: EnrichedAppointmentPatient
      groupSize: number
      isGroupStart: boolean
      isGroupEnd: boolean
    }[] = []
    for (const g of groupPatients(sortedPatients)) {
      g.rows.forEach((p, i) => out.push({
        patient: p,
        groupSize: g.rows.length,
        isGroupStart: i === 0,
        isGroupEnd: i === g.rows.length - 1,
      }))
    }
    return out
  }, [sortedPatients])

  // Distinct humans among the status-filtered rows — the header's filtered
  // patient count next to the coverage count.
  const filteredPatientCount = useMemo(
    () => countPatients(filteredDayPatients),
    [filteredDayPatients],
  )

  // ── Handlers ─────────────────────────────────────────────────────
  const handleRowClick = useCallback((patient: AppointmentPatient) => {
    switch (patient.eligibilityStatus) {
      case 'in_progress': {
        // An auto-retrying check still has a viewable failure doc (why it's
        // retrying) — open it. A genuinely in-flight check (pending, no result
        // yet) has nothing to show.
        if (patient.flowId) {
          const check = checksByFlowId.get(patient.flowId)
          if (check && isAutoRetrying(check)) { onViewCheck?.(patient, check); return }
        }
        return // check is running — nothing to do
      }
      case 'eligible':
      case 'not_eligible':
      case 'needs_review':
      case 'error': {
        if (!patient.flowId) break
        const check = checksByFlowId.get(patient.flowId)
        if (check) { onViewCheck?.(patient, check); return }
        break
      }
    }
    // not_checked, stale, or no check data available — open eligibility form
    onRunCheck?.(patient)
  }, [checksByFlowId, onRunCheck, onViewCheck])

  // Select a day from the rail. Reset to the status sort (who's covered first)
  // and ascending, matching the per-day "today" behavior.
  const handleDaySelect = useCallback((key: string) => {
    setSelectedDate(key)
    setSortKey('status')
    setSortDirection('asc')
  }, [])

  const handleSort = useCallback((key: SortKey) => {
    if (key === sortKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }, [sortKey])

  // For multi-clinic admins, target the filter selection; "All Clinics" disables
  // the button (a single pull can only target one clinic). Single-clinic admins
  // and staff fall back to effectiveClinicId.
  const repullClinicId = isAdmin && showClinicFilter && clinicFilter !== 'all'
    ? clinicFilter
    : effectiveClinicId

  const handleRepull = useCallback(async () => {
    if (isRepulling || !repullClinicId) return

    setIsRepulling(true)
    try {
      // selectedDate is MM/DD/YYYY — the format initiateAppointmentPull expects.
      const { results } = await initiateAppointmentPull({ orgId, clinicId: repullClinicId, appointmentDate: selectedDate })
      const action = results?.[0]?.action

      if (action === 'skipped') {
        const reason = results?.[0]?.reason ?? 'already processed'
        toast.warning(`Skipped: ${scrubVendorText(reason)}.`)
      } else if (action === 'failed') {
        toast.error('Pull triggered but bot failed. It will be retried by the next sweep.')
      } else {
        toast.success('Appointment pull triggered successfully.')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(`Re-pull failed: ${scrubVendorText(msg)}`)
      Sentry.captureException(err)
    } finally {
      setIsRepulling(false)
    }
  }, [isRepulling, orgId, repullClinicId, selectedDate])

  const allClinicsSelected = isAdmin && showClinicFilter && clinicFilter === 'all'
  const repullDisabled = isRepulling || allClinicsSelected || !repullClinicId
  const repullTitle = allClinicsSelected
    ? 'Select a specific clinic to refresh appointments.'
    : !repullClinicId
      ? 'No active clinic to refresh.'
      : 'Re-pull appointments from the EHR'
  const repullDayLabel = selectedDay.label === 'Today' ? 'today'
    : selectedDay.label === 'Tomorrow' ? 'tomorrow'
    : selectedDay.weekday

  // ── Render ───────────────────────────────────────────────────────
  return (
    <main className="dashboard-main cockpit-main">
      <div className="cockpit">
        {/* ── Title bar ── */}
        <div className="cockpit__header">
          <div className="cockpit__title-group">
            <h2 className="cockpit__title">Daily Cockpit</h2>
            <span className="cockpit__date">{dateLabel}</span>
          </div>
          <div className="cockpit__toolbar">
            <div className="cockpit__search-wrapper">
              <span className="cockpit__search-icon"><IconSearch /></span>
              <input
                className="cockpit__search"
                type="search"
                placeholder="Search name, DOB, member ID..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            {isAdmin && showClinicFilter && (
              <select
                className="cockpit__clinic-filter"
                value={clinicFilter}
                onChange={e => setClinicFilter(e.target.value)}
              >
                <option value="all">All Clinics</option>
                {clinics.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="cockpit__repull-btn"
              onClick={handleRepull}
              disabled={repullDisabled}
              title={repullTitle}
            >
              {isRepulling ? <span className="cockpit__spinner cockpit__spinner--inline" /> : null}
              <span>{isRepulling ? 'Refreshing…' : `Refresh ${repullDayLabel}`}</span>
            </button>
          </div>
        </div>

        {/* ── Batch progress bands (#113) ── */}
        {batchBands.length > 0 && (
          <div className="batch-bands">
            {batchBands.map(b => (
              <BatchProgressBand
                key={b.key}
                batch={b}
                clinicName={clinics.length > 1 ? clinics.find(c => c.id === b.clinicId)?.name : undefined}
                etaMs={etaFor(b)}
                isFiltered={batchFilter === b.key}
                onToggleFilter={() => setBatchFilter(f => (f === b.key ? null : b.key))}
                onDismiss={() => dismissBatch(b.key)}
              />
            ))}
          </div>
        )}

        {/* ── 7-day rail ── */}
        <div className="cockpit__rail">
          {weekDays.map(day => (
            <DayTile
              key={day.key}
              day={day}
              summary={daySummaries.get(day.key) ?? computeSummary([])}
              isActive={selectedDate === day.key}
              onClick={() => handleDaySelect(day.key)}
            />
          ))}
        </div>

        {/* ── Loading state ── */}
        {loading && (
          <div className="cockpit__empty">
            <span className="cockpit__spinner" />
            <p>Loading appointments…</p>
          </div>
        )}

        {/* ── Batch filter chips ── */}
        {!loading && batchFilter && (
          <div className="cockpit__batch-chips">
            <button
              type="button"
              className="cockpit__batch-chip cockpit__batch-chip--active"
              onClick={() => setBatchFilter(null)}
              title="Remove the batch filter"
            >
              This batch ({selectedDayPatients.length}) ✕
            </button>
            <button
              type="button"
              className="cockpit__batch-chip"
              onClick={() => setBatchFilter(null)}
            >
              All appointments ({selectedDayAllCount})
            </button>
          </div>
        )}

        {/* ── Patient table ── */}
        {!loading && selectedDayPatients.length === 0 ? (
          <div className="cockpit__empty">
            {searchQuery.trim()
              ? <p>No patients match &ldquo;{searchQuery}&rdquo;</p>
              : enrichedPatients.length === 0
              ? (
                <div className="cockpit__empty-state">
                  <div className="cockpit__empty-icon">
                    <IconCalendar />
                  </div>
                  <p className="cockpit__empty-title">No appointments loaded</p>
                  <p className="cockpit__empty-subtitle">
                    Appointment data from RevolutionEHR hasn&rsquo;t been pulled yet.
                    Once the integration is configured, today&rsquo;s schedule will appear here automatically.
                  </p>
                </div>
              )
              : (
                <div className="cockpit__empty-state">
                  <div className="cockpit__empty-icon">
                    <IconCalendar />
                  </div>
                  <p className="cockpit__empty-title">
                    No appointments for {selectedDay.weekday}, {selectedDay.monthDay}
                  </p>
                  <p className="cockpit__empty-subtitle">
                    Pick another day above, or refresh to re-pull this day&rsquo;s schedule from the EHR.
                  </p>
                </div>
              )
            }
          </div>
        ) : !loading && (
          <div className="cockpit__table-wrapper">
            {/* Table header: selected-day context + status quick-filter chips.
                The chips live on the table card (not a second rail) — they
                filter what the table shows, so they read as a property of it.
                Counts come from the unfiltered day summary. */}
            <div className="cockpit__table-head">
              <div className="cockpit__table-head-day">
                <strong>
                  {selectedDay.label ? `${selectedDay.label} · ` : ''}{selectedDay.weekday}, {selectedDay.monthDay}
                </strong>
                {/* Both truths, labeled: humans AND coverage rows. Filtered,
                    the coverage numbers match the chip counts (chips count
                    coverage-row statuses), with the human count alongside. */}
                <span className="cockpit__table-count">
                  {rollupFilter === 'all'
                    ? `${plural(selectedDaySummary.patients, 'patient')} · ${plural(selectedDaySummary.total, 'coverage')}`
                    : `Showing ${filteredDayPatients.length} of ${selectedDaySummary.total} coverages · ${plural(filteredPatientCount, 'patient')}`}
                </span>
              </div>
              <div className="cockpit__chips" role="group" aria-label="Filter patients by status">
                <button
                  type="button"
                  className={`cockpit__chip ${rollupFilter === 'all' ? 'cockpit__chip--active' : ''}`}
                  aria-pressed={rollupFilter === 'all'}
                  onClick={() => setRollupFilter('all')}
                >
                  All <span className="cockpit__chip-count">{selectedDaySummary.total}</span>
                </button>
                {ROLLUP_ORDER.map(g => {
                  const count = selectedDaySummary[g]
                  return (
                    <button
                      key={g}
                      type="button"
                      className={`cockpit__chip cockpit__chip--${ROLLUP_GROUP_META[g].tone} ${rollupFilter === g ? 'cockpit__chip--active' : ''}`}
                      aria-pressed={rollupFilter === g}
                      disabled={count === 0 && rollupFilter !== g}
                      title={groupBreakdown(g, selectedDaySummary.byStatus)}
                      onClick={() => setRollupFilter(prev => (prev === g ? 'all' : g))}
                    >
                      {ROLLUP_GROUP_META[g].label} <span className="cockpit__chip-count">{count}</span>
                    </button>
                  )
                })}
              </div>
            </div>
            {rollupFilter !== 'all' && filteredDayPatients.length === 0 ? (
              <div className="cockpit__filter-empty">
                <p>No {ROLLUP_GROUP_META[rollupFilter].label.toLowerCase()} patients for this day.</p>
                <button type="button" className="cockpit__filter-empty-clear" onClick={() => setRollupFilter('all')}>
                  Show all {selectedDayPatients.length}
                </button>
              </div>
            ) : (
            <table className="cockpit__table">
              <thead>
                <tr>
                  <SortableTh label="Patient" field="patient" sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableTh label="Payer" field="payer" sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableTh label="Status" field="status" sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableTh label="Provider" field="provider" sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableTh label="Appointment Date" field="appointmentDate" sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {renderRows.map(({ patient, groupSize, isGroupStart, isGroupEnd }, idx) => {
                  // Hover tooltip parity with Recent Checks: terminal pills show REASON_COPY;
                  // Cannot Check shows the per-row eligibilityIssue text (Missing DOB, missing
                  // credentials, etc.). An auto-retrying check (in_progress) explains why it's
                  // waiting; other non-terminal states (stale, not_checked) are self-explanatory.
                  const check = patient.flowId ? checksByFlowId.get(patient.flowId) : undefined
                  const term = check ? readTerminal(check) : null
                  // An auto-retrying check reads as in_progress but still has a failure doc to
                  // show — keep its row openable (not disabled) and give the badge a "why it's
                  // waiting" tooltip instead of the terminal reject reason.
                  const retrying = patient.eligibilityStatus === 'in_progress' && isAutoRetrying(check)
                  const badgeTitle = retrying && check
                    ? retryingReason(check)
                    : patient.eligibilityStatus === 'data_issue'
                    ? (patient.eligibilityIssue ?? undefined)
                    : patient.eligibilityStatus === 'stale'
                      ? 'Most recent check was for a past visit — re-verify for this appointment'
                      : term ? reasonCopyFor(term.reason, check?.type) : undefined
                  return (
                  <tr
                    key={`${patient.id}-${patient.appointmentDate ?? idx}-${patient.clinicId ?? ''}`}
                    className={[
                      'cockpit__row',
                      patient.eligibilityStatus === 'in_progress' && !retrying ? 'cockpit__row--disabled' : '',
                      // Grouped patient: rows before the group's last drop
                      // their border so the group reads as one block…
                      groupSize > 1 && !isGroupEnd ? 'cockpit__row--merged' : '',
                      // …and continuation rows (patient cell left empty)
                      // tuck up under the group's first row.
                      !isGroupStart ? 'cockpit__row--cont' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleRowClick(patient)}
                  >
                    {/* Patient cell only on the group's first row; the click
                        target stays per coverage row. */}
                    <td>
                      {isGroupStart && (
                        <>
                          <div className="cockpit__cell-primary">
                            {patient.lastName}, {patient.firstName}
                          </div>
                          <div className="cockpit__cell-secondary">
                            DOB: {patient.dob ? formatDob(patient.dob) : '—'}
                            {groupSize > 1 ? ` · ${groupSize} coverages` : ''}
                          </div>
                        </>
                      )}
                    </td>
                    <td>
                      <div className="cockpit__cell-primary">
                        {patient.displayPayer ?? '—'}
                        {patient.payerDecidedBy === 'ai' && (
                          <span
                            className="cockpit__ai-badge"
                            data-ai-tooltip={`Original payer name was "${patient.payer}"\nVitalRev AI matched it to this payer`}
                            aria-label={`Original payer name was "${patient.payer}" — VitalRev AI matched it to this payer`}
                          >
                            <IconSparkle />
                          </span>
                        )}
                        {patient.payerDecidedBy === 'staff' && (
                          <span
                            className="cockpit__ai-badge cockpit__staff-badge"
                            data-ai-tooltip={`Original payer name was "${patient.payer}"\nCorrected by staff`}
                            aria-label={`Original payer name was "${patient.payer}" — corrected by staff`}
                          >
                            ✎
                          </span>
                        )}
                      </div>
                      <div className="cockpit__cell-secondary">{patient.policyNumber ?? '—'}</div>
                    </td>
                    <td>
                      <CockpitStatusBadge
                        status={patient.eligibilityStatus}
                        title={badgeTitle}
                        override={retrying && check ? retryingStatusMeta(check) : undefined}
                      />
                    </td>
                    <td>
                      <div className="cockpit__cell-primary">{patient.providerName ?? '—'}</div>
                    </td>
                    <td>
                      <div className="cockpit__cell-primary">
                        {patient.appointmentDate ? formatAppointmentDate(patient.appointmentDate) : '—'}
                      </div>
                      {!patient.appointmentTimeIsDefault
                        && formatAppointmentTimeRange(patient.appointmentStartMin, patient.appointmentEndMin) && (
                        <div className="cockpit__cell-secondary">
                          {formatAppointmentTimeRange(patient.appointmentStartMin, patient.appointmentEndMin)}
                        </div>
                      )}
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
            )}
          </div>
        )}
      </div>
    </main>
  )
}
